import React from 'react';
import { ReferenceData, referenceDataService } from '../services/referenceDataService';

interface TerminologyContextValue {
  data: ReferenceData | null;
  loading: boolean;
  error: string;
  load: () => Promise<void>;
  retry: () => Promise<void>;
}

const TerminologyContext = React.createContext<TerminologyContextValue | null>(null);

export const TerminologyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [data, setData] = React.useState<ReferenceData | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const pending = React.useRef<Promise<void> | null>(null);

  const load = React.useCallback(async () => {
    if (data || pending.current) return pending.current || Promise.resolve();
    setLoading(true);
    setError('');
    pending.current = referenceDataService.load()
      .then(setData)
      .catch(() => {
        setError('Fixed filter options are temporarily unavailable.');
      })
      .finally(() => {
        setLoading(false);
        pending.current = null;
      });
    return pending.current;
  }, [data]);

  const retry = React.useCallback(async () => {
    setData(null);
    setLoading(true);
    setError('');
    await referenceDataService.load()
      .then(setData)
      .catch(() => setError('Fixed filter options are temporarily unavailable.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <TerminologyContext.Provider value={{ data, loading, error, load, retry }}>
      {children}
    </TerminologyContext.Provider>
  );
};

export const useTerminologyContext = (): TerminologyContextValue => {
  const context = React.useContext(TerminologyContext);
  return context || {
    data: null,
    loading: false,
    error: '',
    load: async () => undefined,
    retry: async () => undefined,
  };
};
